import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const SpinSVG = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 15 15"
    fill="none"
    style={{ animation: "spin 0.8s linear infinite", flexShrink: 0 }}
  >
    <circle
      cx="7.5"
      cy="7.5"
      r="5.5"
      stroke="currentColor"
      strokeOpacity=".3"
      strokeWidth="2.5"
      fill="none"
    />
    <path
      d="M7.5 2A5.5 5.5 0 0 1 13 7.5"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

const FormIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
  </svg>
);

function normalizeLabel(text) {
  return String(text || "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * EMS right-drawer form shell.
 * Prefer passing `children` + optional `onSubmit`.
 * Without children, falls back to a compact page header (legacy).
 */
const Header = ({
  mode,
  updateMessage,
  addMessage,
  homeLink,
  homeText,
  onSubmit,
  loading = false,
  btnLoading = false,
  hideFooter,
  children,
}) => {
  const navigate = useNavigate();

  const title =
    mode === "edit"
      ? updateMessage && /^(update|edit)\b/i.test(String(updateMessage).trim())
        ? updateMessage
        : `Update ${updateMessage || homeText || ""}`.trim()
      : mode === "view"
        ? `View ${homeText || updateMessage || ""}`.trim()
        : addMessage && /^(add|create|new)\b/i.test(String(addMessage).trim())
          ? addMessage
          : `Add ${addMessage || homeText || ""}`.trim();

  const subtitle =
    mode === "view"
      ? homeText || ""
      : mode === "edit"
        ? `Editing · ${homeText || ""}`
        : "Fill in the details below";

  const handleClose = useCallback(() => {
    if (homeLink) navigate(homeLink);
    else navigate(-1);
  }, [homeLink, navigate]);

  const handleFooterSubmit = useCallback((e) => {
    if (typeof onSubmit === "function") {
      onSubmit(e);
      return;
    }
    const body = document.querySelector(".ems-form-drawer .ems-fd-body");
    if (!body) return;
    const buttons = [...body.querySelectorAll("button, .MuiButton-root")];
    const submitBtn = buttons.find((b) =>
      /^(submit|update|save|create user|update user|create)$/i.test(
        normalizeLabel(b.textContent)
      )
    );
    if (submitBtn && !submitBtn.disabled) submitBtn.click();
  }, [onSubmit]);

  // Hide legacy Submit/Cancel rows inside drawer body (footer replaces them)
  useEffect(() => {
    if (children == null) return undefined;
    const body = document.querySelector(".ems-form-drawer .ems-fd-body");
    if (!body) return undefined;

    const hide = () => {
      body.querySelectorAll("button, .MuiButton-root").forEach((btn) => {
        const label = normalizeLabel(btn.textContent);
        if (!/^(submit|update|save|cancel|close)$/i.test(label)) return;
        btn.setAttribute("data-ems-legacy-action", "1");

        // Only hide a parent row when it is action-buttons-only.
        // Never hide a container that also holds form fields (common pattern:
        // TextFields + Submit share one MuiGrid-container).
        const row =
          btn.closest(".MuiGrid-container") ||
          btn.closest(".MuiStack-root") ||
          btn.parentElement;
        if (!row || row === body) return;
        const hasFields = row.querySelector(
          "input, textarea, select, .MuiTextField-root, .MuiFormControl-root, .MuiAutocomplete-root, .MuiPickersTextField-root"
        );
        if (hasFields) return;
        const rowButtons = [...row.querySelectorAll("button, .MuiButton-root")];
        if (rowButtons.length === 0) return;
        const allAction = rowButtons.every((b) =>
          /^(submit|update|save|cancel|close)$/i.test(normalizeLabel(b.textContent))
        );
        if (allAction) row.setAttribute("data-ems-legacy-actions", "1");
      });
    };

    hide();
    const obs = new MutationObserver(hide);
    obs.observe(body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, [children, loading]);

  // Legacy full-page forms that only pass title props (no children)
  if (children == null) {
    const crumbLabel =
      mode === "edit" ? "Edit" : mode === "view" ? "View" : "Add";
    return (
      <div className="ems-form-hd">
        <h1 className="ems-page-title">{title}</h1>
        <nav className="ems-crumb" aria-label="breadcrumb">
          <button type="button" className="ems-crumb-link" onClick={handleClose}>
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ flexShrink: 0 }}
            >
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
            {homeText}
          </button>
          <span className="ems-crumb-sep" aria-hidden="true">
            ›
          </span>
          <span className="ems-crumb-current">{crumbLabel}</span>
        </nav>
        <div className="ems-section-divider" />
      </div>
    );
  }

  const showFooter = hideFooter !== true && mode !== "view";

  return (
    <>
      <div className="ems-drawer-backdrop" onClick={handleClose} />

      <div className="ems-form-drawer">
        <div className="ems-fd-head">
          <div className="ems-fd-ic">
            <FormIcon />
          </div>
          <div className="ems-fd-titles">
            <div className="ems-fd-title">{title}</div>
            {subtitle ? <div className="ems-fd-sub">{subtitle}</div> : null}
          </div>
          <button
            type="button"
            className="ems-fd-close"
            onClick={handleClose}
            title="Close"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="ems-fd-body">
          {loading ? (
            <div className="ems-fm-loading">
              <SpinSVG />
            </div>
          ) : (
            <div className="ems-fm-body">{children}</div>
          )}
        </div>

        {showFooter && (
          <div className="ems-fd-foot">
            <button type="button" className="ems-btn ems-ghost" onClick={handleClose}>
              Cancel
            </button>
            <button
              type="button"
              className="ems-btn ems-primary"
              onClick={handleFooterSubmit}
              disabled={loading || btnLoading}
            >
              {loading || btnLoading ? (
                <>
                  <SpinSVG />
                  {mode === "edit" ? "Updating…" : "Submitting…"}
                </>
              ) : mode === "edit" ? (
                "Update"
              ) : (
                "Submit"
              )}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default Header;
